/**
 * Vercel AI SDK Tool Factory for MorphClient
 * 
 * Creates Vercel AI SDK-compatible tools that inherit the API key from MorphClient.
 */

import type { LanguageModel } from 'ai';
import type { MorphClientConfig } from '../client.js';
import { createWarpGrepTool, createGitHubSearchTool } from '../tools/warp_grep/vercel.js';
import { createCodebaseSearchTool } from '../tools/codebase_search/vercel.js';
import { createEditFileTool } from '../tools/fastapply/vercel.js';
import { createExploreSubagent } from '../subagents/vercel.js';
import type { WarpGrepToolConfig, GitHubSearchToolConfig } from '../tools/warp_grep/types.js';
import type { CodebaseSearchConfig } from '../tools/codebase_search/types.js';
import type { EditFileConfig } from '../tools/fastapply/types.js';
import type { ExploreSubagentConfig } from '../subagents/types.js';

/**
 * Factory for creating Vercel AI SDK-compatible tools with inherited API key
 * 
 * @example
 * ```typescript
 * const morph = new MorphClient({ apiKey: process.env.MORPH_API_KEY });
 * 
 * const grepTool = morph.vercel.createWarpGrepTool({ repoRoot: '.' });
 * const searchTool = morph.vercel.createCodebaseSearchTool({ repoId: 'my-project' });
 * const editTool = morph.vercel.createEditFileTool({ baseDir: './src' });
 * 
 * // Use with Vercel AI SDK
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { grep: grepTool, search: searchTool, edit: editTool },
 *   prompt: 'Find and fix the bug'
 * });
 * ```
 */
export class VercelToolFactory {
  constructor(private config: MorphClientConfig) {}

  /**
   * Create a Vercel AI SDK-compatible warp grep tool
   * 
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns Vercel AI SDK tool
   */
  createWarpGrepTool(toolConfig: Omit<WarpGrepToolConfig, 'morphApiKey'>) {
    return createWarpGrepTool({ 
      ...toolConfig, 
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create a Vercel AI SDK-compatible codebase search tool
   * 
   * @param toolConfig - Tool configuration with repoId (apiKey inherited from MorphClient)
   * @returns Vercel AI SDK tool
   */
  createCodebaseSearchTool(toolConfig: Omit<CodebaseSearchConfig, 'apiKey'>) {
    return createCodebaseSearchTool({ 
      ...toolConfig, 
      apiKey: this.config.apiKey,
    });
  }

  /**
   * Create a Vercel AI SDK-compatible edit file tool
   * 
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns Vercel AI SDK tool
   */
  createEditFileTool(toolConfig: Omit<EditFileConfig, 'morphApiKey'> = {}) {
    return createEditFileTool({ 
      ...toolConfig, 
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create a Vercel AI SDK-compatible GitHub search tool
   *
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns Vercel AI SDK tool
   */
  createGitHubSearchTool(toolConfig?: Omit<GitHubSearchToolConfig, 'morphApiKey'>) {
    return createGitHubSearchTool({
      ...toolConfig,
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create an Explore subagent that searches the codebase and returns summaries
   *
   * @param config - Subagent configuration including model (morphApiKey inherited from MorphClient)
   * @returns Explore subagent with .tool, .run(), and .stream() methods
   */
  createExploreSubagent(config: Omit<ExploreSubagentConfig, 'morphApiKey'> & { model: LanguageModel }) {
    return createExploreSubagent({
      ...config,
      morphApiKey: this.config.apiKey,
    });
  }
}

